import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Thermometer, SprayCan, AlertTriangle, FileImage, Settings, FileDown, RefreshCw, Check } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/PageHeader";
import { HaccpCalendar } from "@/components/HaccpCalendar";
import { useAuth } from "@/App";
import { toast } from "sonner";
import { currentMonth, todayISO, fmtDay } from "@/utils/haccp";

const NAV = [
  { to: "/admin/haccp/temperature", label: "Temperature", icon: Thermometer, color: "text-sky-400", bg: "bg-sky-500/10", border: "border-sky-500/20" },
  { to: "/admin/haccp/pulizie", label: "Pulizie", icon: SprayCan, color: "text-emerald-400", bg: "bg-emerald-500/10", border: "border-emerald-500/20" },
  { to: "/admin/haccp/non-conformita", label: "Non conformità", icon: AlertTriangle, color: "text-red-400", bg: "bg-red-500/10", border: "border-red-500/20" },
  { to: "/admin/haccp/schede", label: "Schede produzione", icon: FileImage, color: "text-violet-400", bg: "bg-violet-500/10", border: "border-violet-500/20" },
  { to: "/admin/haccp/impostazioni", label: "Impostazioni", icon: Settings, color: "text-zinc-400", bg: "bg-zinc-500/10", border: "border-zinc-500/20" },
];

const TempSlot = ({ point, slot, defaultTemp, onSave }) => {
  const [val, setVal] = useState(slot.reading ? String(slot.reading.temp) : (defaultTemp ?? ""));
  const [saving, setSaving] = useState(false);
  const r = slot.reading;

  const save = async () => {
    const n = parseFloat(String(val).replace(",", "."));
    if (Number.isNaN(n)) { toast.error("Inserisci una temperatura valida"); return; }
    setSaving(true);
    try { await onSave(point.point_id, slot.slot, n); } finally { setSaving(false); }
  };

  return (
    <div className="flex items-center gap-2 flex-wrap">
      <span className="text-xs text-zinc-400 w-20 capitalize">{slot.slot}</span>
      <Input
        data-testid={`temp-input-${slot.slot}`}
        value={val}
        onChange={(e) => setVal(e.target.value)}
        inputMode="decimal"
        placeholder="°C"
        className="h-10 w-24 bg-[#0f0f11] border-white/10 text-white text-center"
      />
      <Button
        data-testid={`temp-save-${slot.slot}`}
        size="sm"
        onClick={save}
        disabled={saving}
        className="h-10 bg-amber-500 hover:bg-amber-400 text-black"
      >
        {r ? "Aggiorna" : "Salva"}
      </Button>
      {r && (
        <span
          data-testid={`temp-status-${slot.slot}`}
          className={`text-[11px] font-medium whitespace-nowrap ${r.ok ? "text-emerald-400" : "text-red-400"}`}
        >
          {r.ok ? "nei limiti" : "FUORI RANGE"}
        </span>
      )}
    </div>
  );
};

export default function HaccpDashboard() {
  const { authAxios } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [from, setFrom] = useState(`${currentMonth()}-01`);
  const [to, setTo] = useState(todayISO());
  const [exporting, setExporting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await authAxios.get("/haccp/dashboard");
      setData(r.data);
    } catch {
      toast.error("Errore caricamento controlli HACCP");
    } finally { setLoading(false); }
  }, [authAxios]);

  useEffect(() => { load(); }, [load]);

  const saveTemp = async (point_id, slot, temp) => {
    try {
      const r = await authAxios.post("/haccp/temperatures", { point_id, slot, temp, date: todayISO() });
      toast[r.data.ok ? "success" : "warning"](
        r.data.ok ? "Temperatura registrata" : `Temperatura FUORI RANGE (${r.data.min_temp}÷${r.data.max_temp}°C)`
      );
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const toggleCleaning = async (task_id, done) => {
    try {
      await authAxios.post("/haccp/cleanings", { task_id, done, date: todayISO() });
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore salvataggio");
    }
  };

  const exportPdf = async () => {
    if (!from || !to) { toast.error("Seleziona le date Da e A"); return; }
    setExporting(true);
    const t = toast.loading("Generazione registro PDF...");
    try {
      const r = await authAxios.get(`/haccp/export/pdf?date_from=${from}&date_to=${to}`, { responseType: "blob", timeout: 120000 });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `registro_haccp_${from}_${to}.pdf`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success("Registro PDF scaricato", { id: t });
    } catch {
      toast.error("Errore generazione PDF", { id: t });
    } finally { setExporting(false); }
  };

  const dailyGroup = data?.cleanings?.find((g) => g.frequency === "daily");
  const defaultTemp = data
    ? String(Math.round(((data.temp_default_min + data.temp_default_max) / 2) * 10) / 10)
    : "";

  return (
    <div className="min-h-screen bg-[#09090b]" data-testid="haccp-dashboard-page">
      <main className="p-4 sm:p-6 lg:p-8 max-w-3xl mx-auto pb-16">
        <PageHeader title="Controlli HACCP" />

        <div className="flex items-end justify-between gap-3 mb-5 flex-wrap">
          <div>
            <h2 className="font-heading text-xl font-bold text-white">Registro del giorno</h2>
            <p className="text-zinc-400 text-xs">
              {fmtDay(data?.date)} · responsabile: <span className="text-amber-400">{data?.signer?.name || "—"}</span>
            </p>
          </div>
          <Button onClick={load} variant="ghost" size="sm" disabled={loading} data-testid="haccp-refresh-btn" className="text-zinc-400 hover:text-white">
            <RefreshCw className={`w-4 h-4 mr-1 ${loading ? "animate-spin" : ""}`} /> Aggiorna
          </Button>
        </div>

        {data?.closed && (
          <Card className="bg-zinc-500/10 border-zinc-500/30 mb-4" data-testid="haccp-closed-banner">
            <CardContent className="p-3">
              <p className="text-xs text-zinc-400">
                Oggi l&apos;azienda è chiusa (domenica o festivo): nessuna registrazione prevista. Puoi comunque registrare manualmente se hai lavorato.
              </p>
            </CardContent>
          </Card>
        )}

        {/* Temperature di oggi */}
        <Card className="bg-[#18181b] border-sky-500/20 mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <Thermometer className="w-4 h-4 text-sky-400" />
              <h3 className="text-white text-base font-semibold">Temperature</h3>
            </div>
            {(data?.points || []).map((p) => (
              <div key={p.point_id} className="mb-4 last:mb-0" data-testid={`haccp-point-${p.point_id}`}>
                <p className="text-sm text-zinc-300 mb-2">
                  {p.name} <span className="text-zinc-500 text-xs">({p.min_temp}÷{p.max_temp}°C)</span>
                </p>
                <div className="space-y-2">
                  {p.slots.map((s) => (
                    <TempSlot key={`${s.slot}-${s.reading?.temp ?? "new"}`} point={p} slot={s} defaultTemp={defaultTemp} onSave={saveTemp} />
                  ))}
                </div>
              </div>
            ))}
            {!loading && !data?.points?.length && (
              <p className="text-zinc-500 text-sm">Nessun punto di misura configurato.</p>
            )}
          </CardContent>
        </Card>

        {/* Pulizie giornaliere */}
        <Card className="bg-[#18181b] border-emerald-500/20 mb-4">
          <CardContent className="p-4">
            <div className="flex items-center gap-2 mb-3">
              <SprayCan className="w-4 h-4 text-emerald-400" />
              <h3 className="text-white text-base font-semibold">Pulizie di oggi</h3>
            </div>
            <div className="space-y-2">
              {(dailyGroup?.items || []).map((it) => (
                <button
                  key={it.task_id}
                  type="button"
                  data-testid={`haccp-daily-task-${it.task_id}`}
                  onClick={() => toggleCleaning(it.task_id, !it.done)}
                  className={`w-full flex items-center gap-3 p-2.5 rounded-lg border text-left transition-colors ${
                    it.done ? "border-emerald-500/40 bg-emerald-500/10" : "border-white/10 hover:bg-white/5"
                  }`}
                >
                  <span className={`w-5 h-5 rounded flex items-center justify-center shrink-0 ${it.done ? "bg-emerald-500" : "border border-zinc-600"}`}>
                    {it.done && <Check className="w-3.5 h-3.5 text-black" />}
                  </span>
                  <span className={`text-sm ${it.done ? "text-emerald-300" : "text-zinc-300"}`}>{it.name}</span>
                </button>
              ))}
            </div>
            <Link to="/admin/haccp/pulizie" className="block mt-3 text-xs text-emerald-400 hover:underline" data-testid="haccp-all-cleanings-link">
              Vedi anche settimanali, mensili e annuali →
            </Link>
          </CardContent>
        </Card>

        {/* Calendario */}
        <Card className="bg-[#18181b] border-white/10 mb-4">
          <CardContent className="p-4">
            <h3 className="text-white text-base font-semibold mb-3">Calendario del mese</h3>
            <HaccpCalendar cal={data?.calendar} />
            {data?.calendar?.summary && (
              <p className="text-[11px] text-zinc-500 mt-3">
                {data.calendar.summary.days_green} giorni completi · {data.calendar.summary.days_amber} parziali · {data.calendar.summary.days_red} con criticità · {data.calendar.summary.days_closed} chiusi
              </p>
            )}
          </CardContent>
        </Card>

        {/* Alert NC aperte */}
        {data?.open_nc > 0 && (
          <Link to="/admin/haccp/non-conformita" data-testid="haccp-open-nc-alert">
            <Card className="bg-red-500/10 border-red-500/30 mb-4">
              <CardContent className="p-4 flex items-center gap-3">
                <AlertTriangle className="w-5 h-5 text-red-400" />
                <p className="text-sm text-red-300">
                  {data.open_nc} non conformità aperte — richiedono azione correttiva
                </p>
              </CardContent>
            </Card>
          </Link>
        )}

        {/* Export PDF */}
        <Card className="bg-[#18181b] border-amber-500/20 mb-5">
          <CardContent className="p-4">
            <h3 className="text-white text-base font-semibold mb-1">Registro per i controlli</h3>
            <p className="text-xs text-zinc-500 mb-3">PDF unico con temperature, pulizie, non conformità (con foto) e schede archiviate.</p>
            <div className="flex items-end gap-2 flex-wrap">
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">Da</label>
                <Input
                  type="date"
                  data-testid="haccp-export-from"
                  value={from}
                  onChange={(e) => setFrom(e.target.value)}
                  className="h-10 w-36 bg-[#0f0f11] border-white/10 text-white"
                />
              </div>
              <div>
                <label className="block text-[10px] uppercase tracking-wide text-zinc-500 mb-1">A</label>
                <Input
                  type="date"
                  data-testid="haccp-export-to"
                  value={to}
                  onChange={(e) => setTo(e.target.value)}
                  className="h-10 w-36 bg-[#0f0f11] border-white/10 text-white"
                />
              </div>
              <Button onClick={exportPdf} disabled={exporting} data-testid="haccp-export-pdf-btn" className="h-10 bg-amber-500 hover:bg-amber-400 text-black">
                <FileDown className="w-4 h-4 mr-1" /> Scarica PDF
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Navigazione sezioni */}
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
          {NAV.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              data-testid={`haccp-nav-${n.to.split("/").pop()}`}
              className={`flex flex-col items-center gap-2 p-4 rounded-xl bg-[#18181b] border ${n.border} hover:scale-[1.03] active:scale-95 transition-transform`}
            >
              <span className={`p-3 rounded-xl ${n.bg}`}><n.icon className={`w-5 h-5 ${n.color}`} /></span>
              <span className="text-xs text-zinc-300 text-center">{n.label}</span>
            </Link>
          ))}
        </div>
      </main>
    </div>
  );
}
